/**
 * Shared form primitives used by the admin Tools & Articles editors.
 *
 * Deliberately simple: labelled inputs, bilingual AR/EN field pairs, and a
 * JSON textarea. No styling library beyond the existing .sas-input class.
 */

export function Label({ htmlFor, children, required, hint }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[12.5px] font-semibold text-[color:var(--sas-ink)] flex items-center gap-1.5"
    >
      {children}
      {required && <span className="text-[color:#b91c1c]" aria-hidden>*</span>}
      {hint && <span className="text-[11px] font-normal text-[color:var(--sas-ink-2)]">{hint}</span>}
    </label>
  );
}

export function FieldError({ error, id }) {
  if (!error) return null;
  return (
    <p id={id} className="text-[12px] text-[#b91c1c]" data-testid={id}>
      {error}
    </p>
  );
}

export function TextInput({ id, value, onChange, placeholder, type = "text", dir, error, testid, autoComplete = "off" }) {
  return (
    <>
      <input
        id={id}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        dir={dir}
        className="sas-input"
        autoComplete={autoComplete}
        data-testid={testid}
        aria-invalid={error ? "true" : undefined}
      />
      <FieldError error={error} id={testid ? `${testid}-error` : undefined} />
    </>
  );
}

export function TextArea({ id, value, onChange, rows = 4, dir, error, testid, placeholder }) {
  return (
    <>
      <textarea
        id={id}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        dir={dir}
        placeholder={placeholder || ""}
        className="sas-input py-3"
        style={{ height: "auto", minHeight: `${rows * 28}px`, resize: "vertical" }}
        data-testid={testid}
        aria-invalid={error ? "true" : undefined}
      />
      <FieldError error={error} id={testid ? `${testid}-error` : undefined} />
    </>
  );
}

export function SelectInput({ id, value, onChange, options, placeholder = "— Select —", error, testid }) {
  return (
    <>
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="sas-input"
        data-testid={testid}
        aria-invalid={error ? "true" : undefined}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <FieldError error={error} id={testid ? `${testid}-error` : undefined} />
    </>
  );
}

/**
 * Bilingual Arabic + English text field pair. Side-by-side on desktop,
 * stacked on mobile. Accepts `type="textarea"` for long-form fields.
 */
export function BilingualField({
  name,
  label,
  valueAr,
  valueEn,
  onChangeAr,
  onChangeEn,
  type = "text",
  rows = 3,
  required,
  hint,
  errors = {},
}) {
  const idAr = `${name}-ar`;
  const idEn = `${name}-en`;
  return (
    <div className="flex flex-col gap-2">
      <Label required={required} hint={hint}>{label}</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] text-[color:var(--sas-ink-2)] font-semibold uppercase tracking-wide">Arabic</span>
          {type === "textarea" ? (
            <TextArea id={idAr} value={valueAr} onChange={onChangeAr} rows={rows} dir="rtl" testid={`field-${idAr}`} error={errors[`${name}.ar`]} />
          ) : (
            <TextInput id={idAr} value={valueAr} onChange={onChangeAr} dir="rtl" testid={`field-${idAr}`} error={errors[`${name}.ar`]} />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] text-[color:var(--sas-ink-2)] font-semibold uppercase tracking-wide">English</span>
          {type === "textarea" ? (
            <TextArea id={idEn} value={valueEn} onChange={onChangeEn} rows={rows} dir="ltr" testid={`field-${idEn}`} error={errors[`${name}.en`]} />
          ) : (
            <TextInput id={idEn} value={valueEn} onChange={onChangeEn} dir="ltr" testid={`field-${idEn}`} error={errors[`${name}.en`]} />
          )}
        </div>
      </div>
    </div>
  );
}

export function JsonEditor({ id, value, onChange, rows = 16, error, testid, placeholder }) {
  return (
    <>
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir="ltr"
        spellCheck="false"
        placeholder={placeholder}
        className="sas-input py-3 font-mono text-[12.5px] leading-relaxed"
        style={{ height: "auto", minHeight: `${rows * 22}px`, resize: "vertical" }}
        data-testid={testid}
        aria-invalid={error ? "true" : undefined}
      />
      <FieldError error={error} id={testid ? `${testid}-error` : undefined} />
    </>
  );
}

/** Multi-select of tool slugs rendered as a chip toggler. */
export function SlugChipPicker({ allOptions, value, onChange, testid }) {
  const set = new Set(value || []);
  const toggle = (slug) => {
    const next = new Set(set);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {allOptions.map((opt) => {
        const active = set.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`text-[11.5px] rounded-full px-3 py-1.5 border transition-colors ${
              active
                ? "border-[color:var(--sas-ink)] bg-[color:var(--sas-ink)] text-white"
                : "border-[color:var(--sas-line)] text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)]"
            }`}
            aria-pressed={active}
            data-testid={`${testid}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
